import React, { useState } from 'react';
import { Check, CreditCard, Shield, X, Zap, Award, ArrowRight, Beaker, TrendingUp } from 'lucide-react';
import { PRICING_PLANS } from '../../config/pricing';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useUserStore } from '../../stores/userStore';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function DashboardPricing() {
  const [isYearly, setIsYearly] = useState(false);
  const subscription = useSubscriptionStore((state) => state.subscription);
  const isAdmin = useUserStore((state) => state.isAdmin());
  const updateUserRole = useUserStore((state) => state.updateUserRole);
  const { user } = useAuth();

  // Test function to simulate premium upgrade
  const handleTestUpgrade = (planId: string) => {
    if (!user?.email) {
      toast.error('User email not found');
      return;
    }

    let role;
    let subscriptionPlanId;
    
    if (planId === 'premium') {
      role = 'premium';
      subscriptionPlanId = 'premium';
    } else if (planId === 'premium_yearly') {
      role = 'premium_yearly';
      subscriptionPlanId = 'premium_yearly';
    } else if (planId === 'pro') {
      role = 'pro';
      subscriptionPlanId = 'pro';
    } else if (planId === 'pro_yearly') {
      role = 'pro_yearly';
      subscriptionPlanId = 'pro_yearly';
    } else {
      role = 'user';
      subscriptionPlanId = 'free';
    }
    
    updateUserRole(user.email, role);
    
    // Update subscription store
    useSubscriptionStore.getState().updateSubscription({
      planId: subscriptionPlanId,
      isActive: true,
      cancelAtPeriodEnd: false
    });

    toast.success(`Plano ${role} ativado com sucesso! Você agora tem acesso a todos os recursos do plano ${role}.`);
  };

  const handlePlanSelect = (planId: string) => {
    if (isAdmin) {
      toast.error('Contas admin não podem alterar planos');
      return;
    }

    // Prevent selecting free plan if user has a paid plan
    if (planId === 'free') {
      toast.error('Não é possível voltar para o plano gratuito. Seu plano atual será mantido até o fim do período pago.');
      return;
    }

    // Determine if the selected plan is the current plan
    const isCurrentPlan = 
      (planId === 'premium' && (subscription.planId === 'premium' || subscription.planId === 'premium_yearly')) ||
      (planId === 'pro' && (subscription.planId === 'pro' || subscription.planId === 'pro_yearly'));
      
    if (isCurrentPlan && !isYearly) {
      toast.error('Você já está neste plano');
      return;
    }

    // In development, use test upgrade
    if (process.env.NODE_ENV === 'development') {
      const actualPlanId = isYearly ? `${planId}_yearly` : planId;
      handleTestUpgrade(actualPlanId);
      return;
    }

    // In production, redirect to payment link
    const plan = PRICING_PLANS.find(p => p.id === planId);
    if (!plan) return;
    
    const paymentLink = isYearly ? plan.paymentLinks.yearly : plan.paymentLinks.monthly;
    window.location.href = paymentLink;
  };

  // Helper function to check if a plan is the current plan
  const isCurrentPlan = (planId: string) => {
    if (planId === 'premium') {
      return subscription.planId === 'premium' || (subscription.planId === 'premium_yearly' && isYearly);
    } else if (planId === 'pro') {
      return subscription.planId === 'pro' || (subscription.planId === 'pro_yearly' && isYearly);
    }
    return subscription.planId === planId;
  };

  // Function to get the appropriate tag component based on plan
  const getPlanTag = (plan: typeof PRICING_PLANS[0]) => {
    if (!plan.tag) return null;
    
    if (plan.id === 'premium') {
      return (
        <div className="absolute -top-5 inset-x-0 flex justify-center">
          <span className="px-4 py-1 bg-indigo-100 text-indigo-600 text-sm font-medium rounded-full flex items-center shadow-md">
            <Zap className="h-3.5 w-3.5 mr-1 animate-pulse" />
            {plan.tag}
            <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      );
    }
    
    if (plan.id === 'pro') {
      return (
        <div className="absolute -top-5 inset-x-0 flex justify-center">
          <span className="px-4 py-1 bg-green-100 text-green-600 text-sm font-medium rounded-full flex items-center shadow-md">
            <Award className="h-3.5 w-3.5 mr-1" />
            {plan.tag}
            <TrendingUp className="h-3.5 w-3.5 ml-1" />
          </span>
        </div>
      );
    }
    
    return (
      <div className="absolute -top-5 inset-x-0 flex justify-center">
        <span className="px-4 py-1 bg-indigo-100 text-indigo-600 text-sm font-medium rounded-full shadow-md">
          {plan.tag}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Beaker className="h-5 w-5 text-yellow-600" />
              <p className="text-yellow-800 font-medium">Modo de Teste Ativo</p>
            </div>
            <p className="mt-1 text-sm text-yellow-600">
              No ambiente de desenvolvimento, clicar em "Começar Agora" irá simular um pagamento bem-sucedido e atualizar seu papel automaticamente.
            </p>
          </div>
        )}

        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
            <CreditCard className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Planos e Preços</h1>
            <p className="text-gray-600">
              {isAdmin 
                ? 'Conta admin - Acesso total a todos os recursos'
                : 'Escolha o plano ideal para suas necessidades'}
            </p>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          <span className={`text-sm ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
            Mensal
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className="relative w-14 h-7 bg-indigo-600 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <div
              className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${
                isYearly ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
            Anual
          </span>
          <span className="text-sm text-emerald-600 font-medium">
            Economize até 20%
          </span>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Premium Plan Indicator */}
          <div className="hidden md:block absolute left-1/2 top-1/3 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              {/* SVG Arrow pointing to Premium plan */}
              <svg width="120" height="60" viewBox="0 0 120 60" className="absolute -left-16 -top-20">
                <path 
                  d="M10,30 Q60,0 110,30" 
                  fill="none" 
                  stroke="#4F46E5" 
                  strokeWidth="3" 
                  strokeLinecap="round"
                  className="animate-pulse"
                />
                <polygon 
                  points="110,30 100,20 100,40" 
                  fill="#4F46E5" 
                  className="animate-pulse"
                />
                <text x="40" y="15" fill="#4F46E5" fontSize="12" fontWeight="bold">Mais Popular</text>
              </svg>
            </div>
          </div>
          
          {/* Pro Plan Indicator */}
          <div className="hidden md:block absolute right-1/4 top-1/3 transform translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              {/* SVG Arrow pointing to Pro plan */}
              <svg width="120" height="60" viewBox="0 0 120 60" className="absolute -right-16 -top-20">
                <path 
                  d="M10,30 Q60,0 110,30" 
                  fill="none" 
                  stroke="#10B981" 
                  strokeWidth="3" 
                  strokeLinecap="round"
                  className="animate-pulse"
                />
                <polygon 
                  points="110,30 100,20 100,40" 
                  fill="#10B981" 
                  className="animate-pulse"
                />
                <text x="20" y="15" fill="#10B981" fontSize="12" fontWeight="bold">Melhor Custo-Benefício</text>
              </svg>
            </div>
          </div>
          
          {PRICING_PLANS.map((plan, index) => {
            const isPaidUser = subscription.planId.includes('premium') || subscription.planId.includes('pro');
            const isFreePlan = plan.id === 'free';
            const currentPlan = isCurrentPlan(plan.id);
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border-2 transition-all duration-300 group ${
                  plan.popular ? 'border-indigo-600 scale-105 hover:shadow-indigo-100 z-10' : 
                  plan.id === 'pro' ? 'border-green-600 hover:shadow-green-100 z-10' :
                  currentPlan ? 'border-green-600 hover:shadow-green-100' : 'border-gray-100 hover:shadow-gray-100'
                } hover:shadow-xl hover:scale-[1.03] hover:-translate-y-1`}
              >
                {/* Custom tag based on plan type */}
                {getPlanTag(plan)}

                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{plan.name}</h3>
                  <p className="mt-2 text-gray-500">{plan.description}</p>

                  <div className="mt-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                        R${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                      </span>
                      <span className="ml-2 text-gray-500">
                        /{isYearly ? 'ano' : 'mês'}
                      </span>
                    </div>
                    {isYearly && plan.yearlyDiscount > 0 && (
                      <p className="mt-1 text-sm text-emerald-600">
                        Economia de {plan.yearlyDiscount}% no plano anual
                      </p>
                    )}
                  </div>

                  <ul className="mt-8 space-y-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" />
                        ) : (
                          <X className="h-5 w-5 text-gray-300 mt-0.5 mr-3 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-gray-900' : 'text-gray-500'}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={
                      currentPlan || 
                      isAdmin || 
                      (isPaidUser && isFreePlan)
                    }
                    className={`mt-8 w-full py-3 px-4 rounded-lg font-medium transition-all ${
                      currentPlan
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isPaidUser && isFreePlan
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : plan.popular
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 transform hover:scale-105'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200 hover:shadow-md'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {currentPlan 
                      ? 'Plano Atual' 
                      : isPaidUser && isFreePlan
                      ? 'Indisponível'
                      : plan.ctaText}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}